import React, { useState, useRef, useCallback, useEffect, Suspense } from "react";
import { X, GripVertical, Maximize2, Minimize2, Loader, ExternalLink } from "lucide-react";
import theme from "../../styles/theme";
import { WizardHatIcon } from "../common/Brand";

const T = theme.colors;
const MIN_WIDTH = 380;
const MAX_WIDTH_RATIO = 0.92;
const DEFAULT_WIDTH_RATIO = 0.55;

/**
 * InteractivePanel
 * 
 * Slides in from the right. Has a draggable left-edge to resize.
 * Renders any React component passed via the registry.
 * 
 * Props:
 *   isOpen      - boolean
 *   onClose     - () => void
 *   title       - string
 *   description - string (optional)
 *   Component   - React component to render inside
 *   postTitle   - string — the post that launched this
 */
export default function InteractivePanel({ isOpen, onClose, title, description, Component, postTitle }) {
  const [panelWidth, setPanelWidth] = useState(() =>
    Math.max(MIN_WIDTH, Math.round(window.innerWidth * DEFAULT_WIDTH_RATIO))
  );
  const [isMaximized, setIsMaximized] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const panelRef = useRef(null);
  const dragStartX = useRef(0);
  const dragStartWidth = useRef(0);

  // Reset width on window resize
  useEffect(() => {
    const handleResize = () => {
      const maxW = Math.round(window.innerWidth * MAX_WIDTH_RATIO);
      setPanelWidth((prev) => Math.min(prev, maxW));
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Lock body scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [isOpen]);

  // Escape to close
  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === "Escape" && isOpen) onClose();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [isOpen, onClose]);

  // ── Drag logic ──────────────────────────────────────────────────────────────

  const handleDragStart = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
    dragStartX.current = e.clientX || e.touches?.[0]?.clientX || 0;
    dragStartWidth.current = panelWidth;
  }, [panelWidth]);

  useEffect(() => {
    if (!isDragging) return;

    const handleMove = (e) => {
      const clientX = e.clientX || e.touches?.[0]?.clientX || 0;
      const delta = dragStartX.current - clientX;
      const newWidth = Math.max(
        MIN_WIDTH,
        Math.min(
          Math.round(window.innerWidth * MAX_WIDTH_RATIO),
          dragStartWidth.current + delta
        )
      );
      setPanelWidth(newWidth);
      setIsMaximized(false);
    };

    const handleUp = () => setIsDragging(false);

    window.addEventListener("mousemove", handleMove);
    window.addEventListener("mouseup", handleUp);
    window.addEventListener("touchmove", handleMove, { passive: false });
    window.addEventListener("touchend", handleUp);

    return () => {
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("mouseup", handleUp);
      window.removeEventListener("touchmove", handleMove);
      window.removeEventListener("touchend", handleUp);
    };
  }, [isDragging]);

  const toggleMaximize = useCallback(() => {
    if (isMaximized) {
      setPanelWidth(Math.max(MIN_WIDTH, Math.round(window.innerWidth * DEFAULT_WIDTH_RATIO)));
      setIsMaximized(false);
    } else {
      setPanelWidth(Math.round(window.innerWidth * MAX_WIDTH_RATIO));
      setIsMaximized(true);
    }
  }, [isMaximized]);

  const actualWidth = isMaximized
    ? Math.round(window.innerWidth * MAX_WIDTH_RATIO)
    : panelWidth;

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: "fixed", inset: 0,
          background: "rgba(0,0,0,0.6)",
          backdropFilter: "blur(4px)",
          zIndex: 9998,
          transition: "opacity 0.3s",
          opacity: isOpen ? 1 : 0,
        }}
      />

      {/* Panel */}
      <div
        ref={panelRef}
        style={{
          position: "fixed",
          top: 0, right: 0, bottom: 0,
          width: actualWidth,
          maxWidth: "100vw",
          background: "#0a0a14",
          borderLeft: `1px solid ${T.border}`,
          zIndex: 9999,
          display: "flex",
          flexDirection: "column",
          boxShadow: "-8px 0 40px rgba(0,0,0,0.5)",
          transform: isOpen ? "translateX(0)" : "translateX(100%)",
          transition: isDragging ? "none" : "transform 0.35s cubic-bezier(0.4, 0, 0.2, 1)",
          userSelect: isDragging ? "none" : "auto",
        }}
      >
        {/* Drag handle */}
        <div
          onMouseDown={handleDragStart}
          onTouchStart={handleDragStart}
          style={{
            position: "absolute",
            left: -6, top: 0, bottom: 0,
            width: 12,
            cursor: "col-resize",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 10,
          }}
        >
          <div
            style={{
              width: 4, height: 48,
              borderRadius: 2,
              background: isDragging ? T.purple : T.border,
              transition: isDragging ? "none" : "background 0.2s",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <GripVertical
              size={10}
              color={isDragging ? T.violet : T.textDim}
              style={{ opacity: 0.7 }}
            />
          </div>
        </div>

        {/* Header */}
        <div
          style={{
            padding: "16px 20px",
            borderBottom: `1px solid ${T.border}`,
            display: "flex",
            alignItems: "center",
            gap: 12,
            flexShrink: 0,
            background: "linear-gradient(180deg, #0d0d1a, #0a0a14)",
          }}
        >
          {/* Wyzcon badge */}
          <div
            style={{
              width: 32, height: 32, borderRadius: "50%",
              background: theme.gradients.hero,
              display: "flex", alignItems: "center", justifyContent: "center",
              border: `2px solid ${T.purple}`,
              flexShrink: 0,
            }}
          >
            <WizardHatIcon size={16} color="#fff" />
          </div>

          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                fontSize: 10, color: T.violet,
                fontFamily: theme.fonts.mono,
                letterSpacing: 1.5, textTransform: "uppercase",
                marginBottom: 2,
              }}
            >
              Interactive Document
            </div>
            <div
              style={{
                fontSize: 14, fontWeight: 600, color: "#fff",
                whiteSpace: "nowrap", overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {title}
            </div>
            {description && (
              <div style={{ fontSize: 11, color: T.textDim, marginTop: 2 }}>
                {description}
              </div>
            )}
          </div>

          {/* Controls */}
          <button
            onClick={toggleMaximize}
            title={isMaximized ? "Restore" : "Maximize"}
            style={{
              width: 32, height: 32, borderRadius: 8,
              background: T.surface,
              border: `1px solid ${T.border}`,
              display: "flex", alignItems: "center", justifyContent: "center",
              cursor: "pointer", flexShrink: 0,
            }}
          >
            {isMaximized
              ? <Minimize2 size={14} color={T.textMuted} />
              : <Maximize2 size={14} color={T.textMuted} />
            }
          </button>

          <button
            onClick={onClose}
            title="Close (Esc)"
            style={{
              width: 32, height: 32, borderRadius: 8,
              background: T.surface,
              border: `1px solid ${T.border}`,
              display: "flex", alignItems: "center", justifyContent: "center",
              cursor: "pointer", flexShrink: 0,
            }}
          >
            <X size={16} color={T.textMuted} />
          </button>
        </div>

        {/* Origin breadcrumb */}
        {postTitle && (
          <div
            style={{
              padding: "8px 20px",
              borderBottom: `1px solid ${T.border}05`,
              fontSize: 11, color: T.textDim,
              display: "flex", alignItems: "center", gap: 6,
              background: T.surface + "40",
              flexShrink: 0,
            }}
          >
            <ExternalLink size={10} />
            <span>From:</span>
            <span style={{ color: T.violet, fontWeight: 500 }}>{postTitle}</span>
          </div>
        )}

        {/* Content area — scrollable */}
        <div
          style={{
            flex: 1,
            overflow: "auto",
            WebkitOverflowScrolling: "touch",
          }}
        >
          <Suspense
            fallback={
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  height: 300,
                  gap: 12,
                }}
              >
                <Loader
                  size={24}
                  color={T.violet}
                  style={{ animation: "spin 1s linear infinite" }}
                />
                <span style={{ fontSize: 13, color: T.textDim }}>
                  Loading interactive document...
                </span>
                <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
              </div>
            }
          >
            {Component && <Component />}
          </Suspense>
        </div>

        {/* Footer */}
        <div
          style={{
            padding: "10px 20px",
            borderTop: `1px solid ${T.border}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            fontSize: 10,
            color: T.textDim,
            fontFamily: theme.fonts.mono,
            flexShrink: 0,
            background: "#0a0a14",
          }}
        >
          <span>Drag left edge to resize</span>
          <span>ESC to close</span>
        </div>
      </div>
    </>
  );
}
